import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { restaurantService, type Restaurant } from '@/services/restaurant';
import { useReviewStats } from '@/hooks/useReviewStats';
import { getAllOperatingHours } from '@/lib/operatingHours';

export function DesktopStoreInfoPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // 리뷰 통계 계산
  const { calculatedRating, calculatedTotalReviews, isLoadingReviewStats } = useReviewStats(storeId || null);

  // 매장 정보 로드
  useEffect(() => {
    const fetchStoreInfo = async () => {
      if (!storeId) return;
      
      try {
        setIsLoading(true);
        
        const result = await restaurantService.getRestaurantById(storeId);
        if (result.success && result.data) {
          setRestaurant(result.data);
        } else {
          showToast('매장 정보를 불러오는데 실패했습니다');
        }
      } catch (error) {
        console.error('Failed to fetch store info:', error);
        showToast('매장 정보를 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoreInfo();
  }, [storeId]);

  // 요일별 영업시간 정보 가져오기
  const getOperatingHours = () => {
    if (!restaurant) return [];
    
    const dayNames = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
    const operatingHours = getAllOperatingHours(restaurant);

    return operatingHours.map(hours => {
      const dayName = dayNames[hours.day_of_week];
      
      let timeString = '';
      if (hours.is_closed) {
        timeString = '휴무';
      } else {
        const openTime = hours.open_time ? hours.open_time.substring(0, 5) : '';
        const closeTime = hours.close_time ? hours.close_time.substring(0, 5) : '';
        timeString = `${openTime} ~ ${closeTime}`;
      }
      
      return {
        day: dayName,
        time: timeString,
        isToday: hours.day_of_week === new Date().getDay(),
        isClosed: hours.is_closed
      };
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
          <p className="text-[18px] text-gray-600">매장 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-[18px] text-gray-600 mb-6">매장 정보를 찾을 수 없습니다</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 text-[16px] font-medium"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  const operatingHours = getOperatingHours();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors mr-4"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-[24px] font-bold text-gray-800">매장 정보</h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* 메인 컨텐츠 - 2열 레이아웃 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* 왼쪽: 기본 정보 */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-[20px] font-bold text-gray-800 mb-6">기본 정보</h2>
            
            <div className="space-y-6">
              {/* 매장명 */}
              <div>
                <h3 className="text-[14px] font-medium text-gray-500 mb-2">매장명</h3>
                <p className="text-[18px] font-semibold text-gray-800">{restaurant.name}</p>
              </div>
              
              {/* 주소 */}
              <div>
                <h3 className="text-[14px] font-medium text-gray-500 mb-2">주소</h3>
                <p className="text-[16px] text-gray-700">{restaurant.address}</p>
              </div>
              
              {/* 전화번호 */}
              {restaurant.phone && (
                <div>
                  <h3 className="text-[14px] font-medium text-gray-500 mb-2">전화번호</h3>
                  <p className="text-[16px] text-gray-700">{restaurant.phone}</p>
                </div>
              )}
              
              {/* 대표메뉴 */}
              <div>
                <h3 className="text-[14px] font-medium text-gray-500 mb-2">대표메뉴</h3>
                <div className="flex flex-wrap gap-2">
                  {restaurant.bestmenu && (
                    <span className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-[16px] font-medium">
                      {restaurant.bestmenu}
                    </span>
                  )}
                  {restaurant.bestmenu1 && (
                    <span className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-[16px] font-medium">
                      {restaurant.bestmenu1}
                    </span>
                  )}
                  {restaurant.bestmenu2 && (
                    <span className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-[16px] font-medium">
                      {restaurant.bestmenu2}
                    </span>
                  )}
                  {restaurant.bestmenu3 && (
                    <span className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-[16px] font-medium">
                      {restaurant.bestmenu3}
                    </span>
                  )}
                </div>
              </div>
              
              {/* 카테고리 - 필드가 있는 경우에만 표시 */}
            </div>
          </div>

          {/* 오른쪽: 영업시간 */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-[20px] font-bold text-gray-800 mb-6">영업시간</h2>
            
            <div className="space-y-3">
              {operatingHours.map((hours, index) => (
                <div 
                  key={index} 
                  className={`flex items-center justify-between py-3 px-4 rounded-lg ${
                    hours.isToday ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                  }`}
                >
                  <span className={`text-[16px] font-medium ${
                    hours.isToday ? 'text-green-700' : 'text-gray-700'
                  }`}>
                    {hours.day}
                    {hours.isToday && (
                      <span className="ml-2 text-[14px] font-normal">(오늘)</span>
                    )}
                  </span>
                  <span className={`text-[16px] ${
                    hours.isClosed 
                      ? 'text-red-600 font-medium' 
                      : hours.isToday 
                        ? 'text-green-700 font-semibold' 
                        : 'text-gray-600'
                  }`}>
                    {hours.time}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 매장 소개 */}
        {restaurant.description && (
          <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
            <h2 className="text-[20px] font-bold text-gray-800 mb-6">매장 소개</h2>
            <p className="text-[16px] text-gray-700 leading-relaxed whitespace-pre-wrap">
              {restaurant.description}
            </p>
          </div>
        )}

        {/* 추가 정보 */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <h2 className="text-[20px] font-bold text-gray-800 mb-6">추가 정보</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 평점 */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="ml-2 text-[18px] font-bold text-gray-800">
                  {calculatedRating.toFixed(1)}
                </span>
                <span className="ml-1 text-[16px] text-gray-500">/ 5.0</span>
                {isLoadingReviewStats && <span className="text-xs text-gray-500 ml-1">...</span>}
              </div>
              <span className="text-[16px] text-gray-600">
                (리뷰 {calculatedTotalReviews}개)
              </span>
            </div>

            {/* 영업 상태 */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full ${
                  restaurant.is_open ? 'bg-green-500' : 'bg-red-500'
                } mr-3`}></div>
                <span className="text-[16px] font-medium text-gray-700">
                  {restaurant.is_open ? '영업 중' : '영업 종료'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <ToastContainer />
    </div>
  );
}